import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SafeAreaView, StyleSheet, TouchableOpacity, View, Text, Platform } from "react-native";
import MapView from "react-native-maps";
import * as Colors from "../../styles/colors";

import GooglePlacesInput from "../../components/GooglePlacesInput";
import LocalDeviceMarker from "./components/LocalDeviceMarker";
import UnityMarkers from "./components/UnityMarkers";
import IconButton from "../../components/ButtonIcon";

import Loading from "../../components/Loading";
import useLocation from "../../hooks/useLocation";
import {
  CIRCLE_RADIUS_INITIAL,
  LATITUDE_DELTA,
  LONGITUDE_DELTA,
  MAP_REGION_INITIAL,
} from "../../services/Config";
import { MAP_STANDANT_STYLE } from "../../styles/maps";
import { StatusBar } from "expo-status-bar";
import { GlobalContext } from "../../contexts/global";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import UnityList from "../UnityList";
import { IS_WEB } from "@services/Config";

export default function Map({ navigation }) {
  // const [loading, setLoading] = useState({
  //   show: true,
  //   message: "Pegando sua localização",
  // });
  const { setLoading, loading } = useContext(GlobalContext);
  const [marker, setMarker] = useState(null);
  const [mapDelta, setMapDelta] = useState({
    latitudeDelta: LATITUDE_DELTA,
    longitudeDelta: LONGITUDE_DELTA,
  });
  const refMap = useRef();
  const [radiosCircle, setRadiosCircle] = useState(CIRCLE_RADIUS_INITIAL);
  const [showModal, setModal] = useState({ show: false, name: "" });
  const [regionUnity, setRegionUnity] = useState(null);
  const deviceLocation = useLocation();
  const [viewList, setViewList] = useState(false);

  useEffect(() => {
    if (!!deviceLocation) {
      setMarker(deviceLocation);
      setLoading({ show: false, message: "" });
    }
  }, [deviceLocation]);

  function handleShowModal(
    name,
    address,
    openingHours,
    paymentMethods,
    latitude,
    longitude
  ) {
    setModal({
      show: false,
      name: name,
      address: address,
      openingHours: openingHours,
      paymentMethods: paymentMethods,
      latitude: latitude,
      longitude: longitude,
      modalState: setModal,
      modalVisible: handleShowModal,
    });
  }

  useEffect(() => {
    refMap.current?.animateToRegion({ ...regionUnity }, 1500);
  }, [regionUnity]);

  useEffect(() => {
    refMap.current?.animateToRegion(
      {
        ...marker,
        ...mapDelta,
      },
      1000
    );
  }, [marker]);

  useEffect(() => {
    let radiosCircleKm = radiosCircle / 1000;
    let mapDeltaCircle;
    switch (radiosCircleKm) {
      case 21:
        mapDeltaCircle = { latitudeDelta: 5, longitudeDelta: 5 };
        break;
      default:
        mapDeltaCircle = {
          latitudeDelta: LATITUDE_DELTA * (radiosCircleKm / 3),
          longitudeDelta: LONGITUDE_DELTA * (radiosCircleKm / 3),
        };
        break;
    }
    refMap.current?.animateToRegion(
      {
        ...marker,
        ...mapDeltaCircle,
      },
      1000
    );
    setMapDelta(mapDeltaCircle);
  }, [radiosCircle]);

  // Web-specific rendering
  if (IS_WEB) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar style="light" />
        <View style={styles.webContainer}>
          <Text style={styles.webMessage}>
            O mapa não está disponível na versão web. Por favor, use o aplicativo móvel para acessar esta funcionalidade.
          </Text>
          <IconButton
            iconName={"arrow-left"}
            textoBotao={"Voltar"}
            estilo={{ backgroundColor: Colors.SECONDARY, marginTop: 20 }}
            onPress={() => navigation.goBack()}
          />
        </View>
      </SafeAreaView>
    );
  }

  // Rest of the component remains unchanged
  // ... existing code ...

  const MapListButton = ({}) => {
    return (
      <TouchableOpacity
        style={styles.buttonList}
        onPress={() => setViewList(!viewList)}
      >
        <Icon
          name={viewList ? "map-marker" : "format-list-bulleted"}
          size={24}
          color={Colors.WHITE}
        />
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar style="light" />
      {loading.show ? (
        <Loading message={loading.message} />
      ) : (
        <>
          <MapView
            ref={refMap}
            style={styles.map}
            customMapStyle={MAP_STANDANT_STYLE}
            showsUserLocation={true}
            showsMyLocationButton={false}
            showsCompass={false}
            showsScale={false}
            showsBuildings={false}
            showsTraffic={false}
            showsIndoors={false}
            showsPointsOfInterest={false}
          >
            {marker && (
              <LocalDeviceMarker
                coordinate={{ marker, setMarker }}
                radiosCircle={radiosCircle}
              />
            )}
            <UnityMarkers
              coordinate={{ marker, setMarker }}
              radiosCircle={radiosCircle}
              onPressMarker={handleShowModal}
            />
          </MapView>
          <GooglePlacesInput
            onPress={(data, details) => {
              setMarker({
                latitude: details.geometry.location.lat,
                longitude: details.geometry.location.lng,
              });
            }}
          />
          <MapListButton />
          {viewList && (
            <UnityList
              coordinate={{ marker, setMarker }}
              radiosCircle={radiosCircle}
              onPressMarker={handleShowModal}
              onPressUnity={(unity) => {
                setRegionUnity({
                  latitude: unity.latitude,
                  longitude: unity.longitude,
                  latitudeDelta: LATITUDE_DELTA / 10,
                  longitudeDelta: LONGITUDE_DELTA / 10,
                });
                setViewList(false);
              }}
            />
          )}
        </>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.BACKGROUND,
  },
  map: {
    width: "100%",
    height: "100%",
  },
  buttonList: {
    position: "absolute",
    bottom: 20,
    right: 20,
    backgroundColor: Colors.PRIMARY,
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: "center",
    justifyContent: "center",
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  webContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  webMessage: {
    fontSize: 16,
    textAlign: 'center',
    color: Colors.TEXT,
  },
});
